import { Button, cn, Typography } from '@prenuvo/halo-foundation';

import { FCT } from '@/i18n';

import { REASON_FOR_ACCESS } from './patient-contact-dialog.constant';
import { useCreateActivityLog } from './use-create-activity-log.hook';

export type PatientContactConfirmationSectionProps = {
  onCancel: () => void;
  onNext: () => void;
  reason: null | string;
  setReason: (reason: string) => void;
};

export function PatientContactConfirmationSection({
  onCancel,
  onNext,
  reason,
  setReason,
}: PatientContactConfirmationSectionProps) {
  const createActivityLog = useCreateActivityLog();

  const onNextButtonHandler = () => {
    const payload = {
      action: 'CREATE' as const,
      fullObject: { reasonForAccessingPtInfo: reason },
      objectDiff: null,
    };

    createActivityLog(payload, () => onNext());
  };

  return (
    <div>
      <Typography as="p" className="text-neutral-50">
        {FCT.REPORTING.HEADER.CONTACT_DETAILS.INFO_DESCRIPTION}
      </Typography>
      <ul className="list-disc pl-6 pt-3">
        <li className="pt-1">
          <Typography className="text-neutral-50">
            {FCT.REPORTING.HEADER.CONTACT_DETAILS.LABEL.LEGAL_NAME}
          </Typography>
        </li>
        <li className="pt-1">
          <Typography className="text-neutral-50">
            {FCT.REPORTING.HEADER.CONTACT_DETAILS.LABEL.PHONE_NUMBER}
          </Typography>
        </li>
      </ul>
      <div className="flex flex-col items-center pt-4">
        <div className="flex">
          <Typography className="text-neutral-400 dark:text-neutral-400">
            {FCT.REPORTING.HEADER.CONTACT_DETAILS.REASON_TITLE}
          </Typography>
          <span className="ml-1 text-red-500 after:content-['*'] dark:text-red-500" />
        </div>
        <div className="flex pt-3">
          {REASON_FOR_ACCESS.map((label) => (
            <Button
              key={label}
              className={{
                root: cn(
                  'rounded-xl border border-neutral-800 dark:border-neutral-800 hover:border-blue-500 dark:hover:border-blue-500 hover:bg-blue-500/10 dark:hover:bg-blue-500/10 mr-3',
                  {
                    'bg-blue-500/10 border-blue-500 dark:bg-blue-500/10 dark:border-blue-500':
                      reason === label,
                  },
                ),
                text: 'text-base font-normal',
              }}
              data-testid={`${label}-btn`}
              onClick={() => setReason(label)}
              size="md"
              variant="outlined"
            >
              {label}
            </Button>
          ))}
        </div>
      </div>
      <div className="mt-6 flex justify-end gap-4">
        <Button
          className={{ root: 'px-8' }}
          data-testid="cancel-button"
          onClick={onCancel}
          variant="outlined"
        >
          {FCT.REPORTING.HEADER.CONTACT_DETAILS.LABEL.CANCEL}
        </Button>
        <Button
          className={{ root: 'px-8' }}
          data-testid="next-button"
          disabled={!reason}
          onClick={onNextButtonHandler}
          variant="primary"
        >
          {FCT.REPORTING.HEADER.CONTACT_DETAILS.LABEL.NEXT}
        </Button>
      </div>
    </div>
  );
}
